import type { StateClassKey } from "@tdelivr/design-tokens";

import type { BoardRow } from "./board.ts";

/**
 * Merchants: the shops trading in this tenant, and the one command that lets a new one trade.
 *
 * ## Why this board exists
 *
 * MER-01 publishes `POST /v1/merchants` under `merchant:create`, MER-02 publishes
 * `POST /v1/merchant-locations` under `merchant-location:create`, and **no screen on the platform
 * spent either** — so under ADR 0032 no client held them, correctly. Nothing could bring a merchant
 * into being.
 *
 * The consequence ran further than an empty board. The merchant portal reads a `merchant_id` claim
 * to know which shop it is acting for, and a claim is a value on an account that has to point at a
 * record; with no merchant anywhere, the portal's six boards drew their designed empty states and
 * the rider's merchant, product and cart screens had nothing to shop from. Measured before this:
 * **zero merchants in any tenant.**
 *
 * ## Why the tenant administers them, which Document 01 does not say
 *
 * §265 lists what a tenant must be able to do — manage drivers, manage vehicles, configure
 * services, prices and zones, monitor trips, view financial reporting — and does not mention
 * merchants. So unlike the driver-onboarding board beside it, this one has no requirements anchor,
 * and the decision was taken on the record's own scoping: `merchant.tenantId` makes a merchant a
 * thing inside one tenant, and this console already administers that tenant's catalogue, pricing,
 * zones, staff, customers and drivers. A platform-console board would have been acting inside one
 * tenant's data from outside it.
 *
 * Recorded with Q-236's answer rather than silently: the alternatives considered were the platform
 * console, on the grounds that `pending_review` reads like a platform vetting step, and seeding
 * with a hand-granted scope, which is what ADR 0032 exists to refuse.
 *
 * ## No design draws this board either
 *
 * `GUI_Design/SCREEN_SPEC.md` draws a merchant **portal**, for a merchant's own staff, and a rider's
 * list of merchants to shop from. It draws nothing anywhere that admits a merchant to a tenant. So
 * this applies the console's own conventions exactly as the driver-onboarding board does, is marked
 * `commissioned`, and sits outside the 110-of-114 screen ratio.
 */

/** The listing's scope. */
export const MERCHANT_READ_SCOPE = "merchant:read";

/** The create. This board is the only thing on the platform that spends it. */
export const MERCHANT_CREATE_SCOPE = "merchant:create";

/** The activation, which is a status transition rather than an update. */
export const MERCHANT_LIFECYCLE_SCOPE = "merchant:lifecycle";

/** The branches read, so a row can say how many a shop has. */
export const LOCATION_READ_SCOPE = "merchant-location:read";

/**
 * Why a merchant is being activated, in a word.
 *
 * MER-01 requires a `reason` on every status change and publishes no vocabulary for it, so this is
 * the console's own — the same position `driver-onboarding.ts` is in with DRV-03's decision reason,
 * and worth knowing before reading one out of an audit log as though it were an enum.
 */
export const ACTIVATED_BY_OPERATOR = "Admitted to trade by the tenant administrator.";

/** One merchant, reduced to what this board reads. */
export interface MerchantRecord {
  readonly id: string;
  readonly tradingName: string;
  readonly legalName: string;
  readonly registrationNumber: string;
  readonly contactEmail: string;
  readonly status: "pending_review" | "active" | "suspended" | "closed";
  readonly statusReason?: string | undefined;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * The words and the state class for each of MER-01's four statuses.
 *
 * The classes are the design's own nine from `@tdelivr/design-tokens`, not a vocabulary invented
 * here. `waiting` for a shop whose admission has not been decided, `done` for one trading,
 * `failed` for one stopped — suspended and closed are drawn alike because both mean *not trading*,
 * and the row's detail panel carries which and why.
 */
const STATUS_WORDS = {
  pending_review: { word: "Awaiting review", classKey: "waiting" },
  active: { word: "Trading", classKey: "done" },
  suspended: { word: "Suspended", classKey: "failed" },
  closed: { word: "Closed", classKey: "failed" },
} as const satisfies Readonly<
  Record<MerchantRecord["status"], { readonly word: string; readonly classKey: StateClassKey }>
>;

/** What an administrator reads in the state column. */
export function statusWords(status: MerchantRecord["status"]): {
  readonly word: string;
  readonly classKey: StateClassKey;
} {
  return STATUS_WORDS[status];
}

/**
 * The merchant the activate command would act on.
 *
 * Only a `pending_review` shop can be admitted: MER-01's transition takes `active | suspended |
 * closed`, and offering `active` over a shop that is already trading is a button that does
 * nothing. A suspended shop is reinstated by the same operation and is deliberately not offered
 * here — reinstatement is a judgement about why it was suspended, which this board does not show.
 */
export function admissible(
  merchants: readonly MerchantRecord[],
  selected: string | undefined,
): MerchantRecord | undefined {
  const waiting = merchants.filter((merchant) => merchant.status === "pending_review");
  if (selected !== undefined) return waiting.find((merchant) => merchant.id === selected);
  return waiting.length === 1 ? waiting[0] : undefined;
}

/** One board row, from a merchant and however many branches MER-02 answered for it. */
export function merchantRow(merchant: MerchantRecord, branches: number | "unread"): BoardRow {
  const words = statusWords(merchant.status);

  return {
    id: merchant.id,
    cells: [
      { text: merchant.tradingName },
      { text: words.word },
      /*
       * A branch count the read did not answer is drawn as the design's `—` rather than as zero.
       * A shop with no branches and a shop whose branches could not be read are different facts,
       * and collapsing them is the absence-reported-as-a-value fault this repository keeps finding.
       */
      branches === "unread"
        ? { text: "—", absent: true, title: "MER-02 did not answer for this merchant" }
        : { text: String(branches) },
    ],
    state: { classKey: words.classKey, label: words.word },
    detail: [
      { label: "Merchant", value: merchant.id, mono: true },
      { label: "Legal name", value: merchant.legalName },
      { label: "Registration", value: merchant.registrationNumber, mono: true },
      { label: "Contact", value: merchant.contactEmail },
      ...(merchant.statusReason === undefined
        ? []
        : [{ label: "Status reason", value: merchant.statusReason }]),
      {
        label: "Branches",
        value: branches === "unread" ? "MER-02 did not answer" : String(branches),
        ...(branches === "unread" || branches === 0 ? { absent: true } : {}),
      },
    ],
    history: [
      { label: "Created", value: merchant.createdAt },
      { label: "Last changed", value: merchant.updatedAt },
      { label: "Version", value: String(merchant.version), mono: true },
    ],
    glyph: "storefront",
  };
}

/** MER-02's create, which this board collects in one form. */
export const LOCATION_CREATE_SCOPE = "merchant-location:create";

/**
 * The three ways a branch can be ordered from, exactly as MER-02 publishes them.
 *
 * Not a vocabulary chosen here: the contract's own enum, at least one and at most three. A
 * board offering a fourth would be inventing a fulfilment the platform cannot carry out, and
 * one offering fewer would hide a choice the shop is entitled to make.
 */
export const FULFILMENT_MODES = [
  { value: "pickup", label: "Collection" },
  { value: "merchant_delivery", label: "The shop delivers" },
  { value: "courier_delivery", label: "A courier delivers" },
] as const;

/** What an operator types to give a shop its first branch. */
export interface BranchDraft {
  readonly branchCode: string;
  readonly name: string;
  readonly addressLabel: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly timeZone: string;
  /** Comma-separated, because one `onChange` serves every field kind in the form. */
  readonly fulfillmentModes: string;
  readonly preparationMinutes: string;
}

/**
 * Whether a branch draft can be sent, and what is wrong with it if not.
 *
 * Checked here rather than left to the service, because MER-02 answers one refusal for a body
 * that breaks several rules and an operator would fix them one round trip at a time. Every
 * bound below is the contract's own — the code's character set, the name's two-character floor,
 * the coordinate ranges, the preparation ceiling of a full day — and none is invented.
 */
export function branchRefusal(draft: BranchDraft): string | undefined {
  if (!/^[A-Za-z0-9_-]+$/.test(draft.branchCode)) {
    return "A branch code is letters, digits, hyphens and underscores, with no spaces.";
  }
  if (draft.name.trim().length < 2) return "A branch name needs at least two characters.";
  if (draft.addressLabel.trim().length < 3) return "An address needs at least three characters.";

  const latitude = Number(draft.latitude);
  const longitude = Number(draft.longitude);
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    return "Latitude is a number between -90 and 90.";
  }
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    return "Longitude is a number between -180 and 180.";
  }
  if (draft.timeZone.trim().length < 3) return "A time zone is needed, such as Africa/Nairobi.";

  const modes = draft.fulfillmentModes.split(",").filter(Boolean);
  if (modes.length === 0) return "Choose at least one way this branch can be ordered from.";

  const minutes = Number(draft.preparationMinutes);
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
    return "Preparation time is a whole number of minutes, from 0 to 1440.";
  }
  return undefined;
}

/** The states present in the fetched rows, so the filter offers only what it can show. */
export function statesPresent(rows: readonly BoardRow[]): readonly string[] {
  return [...new Set(rows.map((row) => row.state.label))].sort();
}
